import { observable, action, computed } from 'mobx'
import {
  ICompany,
  IBatchResponse,
  API,
  IInventoryAuditBatchContract,
  IInventoryAuditProductContract,
  AuditBatchAPI,
  AuditProductAPI,
} from '@getgreenline/homi-shared'
import { message } from 'antd'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { AuditModels, AuditProductModels } from '@getgreenline/audits'

export type TActionType = 'delete' | 'refresh'

export type ICachedAuditProductContract<T extends 'enteredQuantity' | 'notes'> =
  T extends 'enteredQuantity'
    ? Omit<IInventoryAuditProductContract, 'notes'>
    : Omit<IInventoryAuditProductContract, 'enteredQuantity'>

interface IAuditBatchSyncCache {
  syncAttempted: boolean
  syncSuccessful: boolean | undefined
  cachedAuditBatchContract: IInventoryAuditBatchContract
}

interface IAuditProductSyncCache<T extends 'enteredQuantity' | 'notes'> {
  syncAttempted: boolean
  syncSuccessful: boolean | undefined
  cachedAuditProductContract: ICachedAuditProductContract<T>
}

class SyncCacheStore {
  @observable
  private auditBatchSyncCache: Map<number, IAuditBatchSyncCache> = new Map()

  @observable
  private auditProductQuantitySyncCache: Map<number, IAuditProductSyncCache<'enteredQuantity'>> =
    new Map()

  @observable
  private auditProductNotesSyncCache: Map<number, IAuditProductSyncCache<'notes'>> = new Map()

  company: ICompany

  audit: AuditModels.CreateInventoryAudit

  protected canEditAudit: boolean

  constructor(company: ICompany, audit: AuditModels.CreateInventoryAudit, canEditAudit: boolean) {
    this.company = company
    this.audit = audit
    this.canEditAudit = canEditAudit
  }

  getAuditBatchSyncCache = (auditBatchId: number) => this.auditBatchSyncCache.get(auditBatchId)

  @action
  setAuditBatchSyncCache = (batchSyncCache: IAuditBatchSyncCache) => {
    this.auditBatchSyncCache.set(batchSyncCache.cachedAuditBatchContract.id!, batchSyncCache)
  }

  @action
  deleteAuditBatchSyncCache = (auditBatchId: number) =>
    this.auditBatchSyncCache.delete(auditBatchId)

  getAuditProductSyncCache = <T extends 'enteredQuantity' | 'notes'>(
    entity: T,
    auditProductId: number,
  ): IAuditProductSyncCache<T> | undefined => {
    const cache: any =
      entity === 'enteredQuantity'
        ? this.auditProductQuantitySyncCache.get(auditProductId)
        : this.auditProductNotesSyncCache.get(auditProductId)

    return cache
  }

  @action
  setAuditProductEQSyncCache = (
    auditProductSyncCache: IAuditProductSyncCache<'enteredQuantity'>,
  ) => {
    const { cachedAuditProductContract, syncAttempted, syncSuccessful } = auditProductSyncCache

    this.auditProductQuantitySyncCache.set(cachedAuditProductContract.id!, {
      syncAttempted,
      syncSuccessful,
      cachedAuditProductContract,
    })
  }

  @action
  setAuditProductNotesSyncCache = (auditProductSyncCache: IAuditProductSyncCache<'notes'>) => {
    const { cachedAuditProductContract, syncAttempted, syncSuccessful } = auditProductSyncCache

    this.auditProductNotesSyncCache.set(cachedAuditProductContract.id!, {
      syncAttempted,
      syncSuccessful,
      cachedAuditProductContract,
    })
  }

  @action
  deleteAuditProductSyncCache = (entity: 'enteredQuantity' | 'notes', auditProductId: number) => {
    return entity === 'enteredQuantity'
      ? this.auditProductQuantitySyncCache.delete(auditProductId)
      : this.auditProductNotesSyncCache.delete(auditProductId)
  }

  @action
  syncAllRecords = async () => {
    for (const unSyncedAuditBatchContract of this.unSyncedAuditBatchContracts) {
      const syncedAuditBatch = await AuditBatchAPI.editAuditBatch(
        this.company.id,
        this.audit.id,
        unSyncedAuditBatchContract,
      )

      this.setAuditBatchSyncCache({
        syncAttempted: true,
        syncSuccessful: true,
        cachedAuditBatchContract: syncedAuditBatch,
      })
    }

    for (const unSyncedAuditProductEQContract of this.unSyncedAuditProductEQContracts) {
      const syncedEQContract = await AuditProductAPI.patchAuditProduct(
        this.company.id,
        unSyncedAuditProductEQContract,
      )

      this.setAuditProductEQSyncCache({
        syncAttempted: true,
        syncSuccessful: true,
        cachedAuditProductContract: syncedEQContract,
      })
    }

    for (const unSyncedAuditProductNotesContract of this.unSyncedAuditProductNotesContracts) {
      const syncedNotesContract = await AuditProductAPI.patchAuditProduct(
        this.company.id,
        unSyncedAuditProductNotesContract,
      )

      this.setAuditProductNotesSyncCache({
        syncAttempted: true,
        syncSuccessful: true,
        cachedAuditProductContract: syncedNotesContract,
      })
    }
  }

  @computed
  private get unSyncedAuditBatchContracts() {
    const unSyncedAuditBatches: IInventoryAuditBatchContract[] = []

    this.auditBatchSyncCache.forEach((auditBatchCache) => {
      if (auditBatchCache.syncSuccessful === false) {
        unSyncedAuditBatches.push(auditBatchCache.cachedAuditBatchContract)
      }
    })

    return unSyncedAuditBatches
  }

  @computed
  private get unSyncedAuditProductEQContracts() {
    const unSyncedContracts: Array<ICachedAuditProductContract<'enteredQuantity'>> = []

    this.auditProductQuantitySyncCache.forEach((auditProductQuantityCache) => {
      if (auditProductQuantityCache.syncSuccessful === false) {
        unSyncedContracts.push(auditProductQuantityCache.cachedAuditProductContract)
      }
    })

    return unSyncedContracts
  }

  @computed
  private get unSyncedAuditProductNotesContracts() {
    const unSyncedContracts: Array<ICachedAuditProductContract<'notes'>> = []

    this.auditProductNotesSyncCache.forEach((auditProductNotesCache) => {
      if (auditProductNotesCache.syncSuccessful === false) {
        unSyncedContracts.push(auditProductNotesCache.cachedAuditProductContract)
      }
    })

    return unSyncedContracts
  }

  @computed
  get unsyncedContracts() {
    return [
      ...this.unSyncedAuditBatchContracts,
      ...this.unSyncedAuditProductEQContracts,
      ...this.unSyncedAuditProductNotesContracts,
    ]
  }

  @computed
  get unSynceRecordExists(): boolean {
    return this.unsyncedContracts.length > 0
  }
}

class BatchOptionStore extends SyncCacheStore {
  @observable
  protected mappedBatchOptionsPerProductId: Map<string, IBatchResponse[]> = new Map()

  constructor(company: ICompany, audit: AuditModels.CreateInventoryAudit, canEditAudit: boolean) {
    super(company, audit, canEditAudit)
  }

  @action
  setAvailableBatchOptions = async (productId: string) => {
    try {
      const availableBatchOptions = await API.getAvailableAuditBatches(
        this.company.id,
        this.audit.id,
        productId,
      )

      this.mappedBatchOptionsPerProductId.set(productId, availableBatchOptions)
    } catch (error) {
      message.error(parseErrorMsg(error) || `There was an error loading the available lot numbers.`)
    }
  }

  getAllBatchOptionsForProduct = (productId: string): IBatchResponse[] | undefined =>
    this.mappedBatchOptionsPerProductId.get(productId)

  @action
  createNewBatchOption = async (
    product: AuditProductModels.EditAuditProduct,
    batchNumber: string,
  ): Promise<IBatchResponse> => {
    const availableBatchOptions = this.getAllBatchOptionsForProduct(product.productId) || []

    const newBatchOptions = await API.addBatches(
      this.company.id,
      this.audit.id,
      product.productId,
      [batchNumber],
    )

    this.mappedBatchOptionsPerProductId.set(product.productId, [
      ...availableBatchOptions,
      ...newBatchOptions,
    ])

    return newBatchOptions[0]
  }

  @action
  getMatchingBatchOption = (productId: string, batchNumber: string): IBatchResponse | undefined => {
    const availableBatchOptions = this.getAllBatchOptionsForProduct(productId) || []

    if (availableBatchOptions) {
      return availableBatchOptions.find((batch) => batch.batchNumber === batchNumber)
    }
  }
}

export class AuditStore extends BatchOptionStore {
  @observable
  isEditable = false

  @observable
  showAuditproductsWithoutSnapshot = false

  constructor(company: ICompany, audit: AuditModels.CreateInventoryAudit, canEditAudit: boolean) {
    super(company, audit, canEditAudit)
  }

  @action
  setShowAuditProductsWithoutSnapshot = (showAuditproductsWithoutSnapshot: boolean) => {
    this.showAuditproductsWithoutSnapshot = showAuditproductsWithoutSnapshot
  }

  @action
  setEditable = () => {
    const editable =
      !(this.audit.status === 'completed' || this.audit.status === 'cancelled') && this.canEditAudit
    this.isEditable = editable
  }

  @computed
  get shouldDisplayQuantity(): boolean {
    return this.company.auditMode === 'regular' || this.audit.status === 'completed'
  }

  @computed
  get containsBatchProduct(): boolean {
    return !!this.audit.products.find((product) => product.isBatchTracked)
  }

  @computed
  get auditProductsWithoutSnapshot() {
    return this.audit.products.filter((product) => product.expectedQuantity === null)
  }
}
